import classNames from "classnames";
import React from "react";
import KuikaBadge, { KuikaBadgeColor } from "../kuika-badge/kuika-badge";
import KuikaSvg from "../kuika-svg/kuika-svg";
import "./kuika-nested-badge.scss";

export interface KuikaNestedBadgeProps {
  /** The parent/group label */
  groupLabel: string;
  /** Array of child labels to display inside the group */
  children: string[];
  /** Color for the parent badge */
  groupColor?: KuikaBadgeColor;
  /** Color for child badges */
  childColor?: KuikaBadgeColor;
  /** Callback when the group close button is clicked (removes all children) */
  onRemoveGroup?: () => void;
  /** Callback when an individual child close button is clicked */
  onRemoveChild?: (childLabel: string) => void;
  /** Custom class name */
  className?: string;
  /** Whether to show close buttons on children (default: true) */
  showChildClose?: boolean;
}

/**
 * KuikaNestedBadge - A badge component that displays a parent badge containing child badges
 *
 * Features:
 * - Parent badge with all children nested inside
 * - Single close button on parent removes entire group
 * - Optional individual close buttons on children
 * - Customizable colors for parent and children
 */
const KuikaNestedBadge: React.FC<KuikaNestedBadgeProps> = ({
  groupLabel,
  children,
  groupColor = "purple",
  childColor = "blue",
  onRemoveGroup,
  onRemoveChild,
  className,
  showChildClose = true
}) => {
  return (
    <div className={classNames("kuika-nested-badge", className)}>
      <KuikaBadge
        content={
          <div className="kuika-nested-badge__content">
            <span className="kuika-nested-badge__group-label">{groupLabel}</span>
            <div className="kuika-nested-badge__children">
              {children.map((child: string, index: number) => (
                <KuikaBadge
                  key={`${child}-${index}`}
                  content={child}
                  color={childColor}
                  rightIcon={
                    showChildClose && onRemoveChild ? (
                      <button
                        type="button"
                        className="kuika-nested-badge__child-close"
                        aria-label={`Remove ${child}`}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                          event.stopPropagation();
                          onRemoveChild(child);
                        }}
                      >
                        <KuikaSvg name="x" width={10} height={10} />
                      </button>
                    ) : undefined
                  }
                />
              ))}
            </div>
          </div>
        }
        color={groupColor}
        rightIcon={
          onRemoveGroup ? (
            <button
              type="button"
              className="kuika-nested-badge__group-close"
              aria-label={`Remove ${groupLabel}`}
              onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                event.stopPropagation();
                onRemoveGroup();
              }}
            >
              <KuikaSvg name="x" width={12} height={12} />
            </button>
          ) : undefined
        }
      />
    </div>
  );
};

export default KuikaNestedBadge;
